import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AcceptInviteForm } from "@/components/teams/AcceptInviteForm";
import { useAuthUser } from "@/context/AuthContext";
import { maskColombianPhone } from "@/lib/phone";

type Search = { code?: string };

export const Route = createFileRoute("/teams/accept")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    code:
      typeof search.code === "string"
        ? search.code.replace(/\D+/g, "").slice(0, 6) || undefined
        : undefined,
  }),
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  return (
    <RequireAuth>
      <AppShell>
        <AcceptInviteContent />
      </AppShell>
    </RequireAuth>
  );
}

function AcceptInviteContent() {
  const { t } = useTranslation();
  const me = useAuthUser();
  const { code } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-6 pt-6">
      <div className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("invitations.accept.title")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("invitations.accept.subtitle")}
        </p>
      </div>

      <Alert variant="info">
        <Phone className="h-4 w-4" />
        <AlertTitle>{t("invitations.accept.session.title")}</AlertTitle>
        <AlertDescription>
          {t("invitations.accept.session.body", {
            phone: maskColombianPhone(me.phone),
          })}
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("invitations.accept.card_title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AcceptInviteForm
            initialCode={code}
            onSuccess={(team) => {
              // Defer navigation so the success state has a beat to render.
              window.setTimeout(() => {
                void navigate({
                  to: "/teams/$teamId",
                  params: { teamId: team.teamId },
                });
              }, 800);
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
